<template>
  <div class="signup-view">
    <!-- Sign-up header -->
    <div class="auth-welcome">
      <h1 class="welcome-title">
        {{ $t('auth.join-indiefy') }}
      </h1>
      <p class="welcome-subtitle">
        {{ $t('landing.no-credit-card') }}
      </p>
    </div>

    <form @submit.prevent="submitSignup">
      <!-- Personal details section -->
      <div class="form-divider mb-4">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.personal-info') }}</span>
        <div class="divider-line" />
      </div>

      <div class="columns">
        <div class="column">
          <div class="indiefy-field">
            <BaseInput
              v-model="firstName"
              :label="$t('auth.first-name')"
              :placeholder="$t('auth.first-name')"
              type="text"
              data-test="first-name-input"
              :has-error="!!formErrors.firstName"
              :error-message="formErrors.firstName"
              left-icon="fas fa-user"
              has-floating-label
              required
              @blur="validateFirstName"
            />
          </div>
        </div>

        <div class="column">
          <div class="indiefy-field">
            <BaseInput
              v-model="lastName"
              :label="$t('auth.last-name')"
              :placeholder="$t('auth.last-name')"
              type="text"
              data-test="last-name-input"
              :has-error="!!formErrors.lastName"
              :error-message="formErrors.lastName"
              left-icon="fas fa-user"
              has-floating-label
              required
              @blur="validateLastName"
            />
          </div>
        </div>
      </div>

      <div class="indiefy-field">
        <BaseInput
          v-model="email"
          :label="$t('auth.email')"
          :placeholder="$t('auth.email')"
          type="email"
          data-test="email-input"
          :has-error="!!formErrors.email"
          :error-message="formErrors.email"
          left-icon="fas fa-envelope"
          has-floating-label
          required
          autocomplete="email"
          @blur="validateEmail"
        />
      </div>

      <div class="columns is-mobile">
        <div class="column is-4-desktop is-5-tablet is-5-mobile">
          <div class="indiefy-field">
            <div class="country-code-dropdown">
              <select
                v-model="phoneCode"
                class="country-select"
                required
                data-test="phone-code-input"
                @blur="validatePhoneCode"
              >
                <option
                  v-for="country in countryCodes"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.flag }} {{ country.name }} ({{ country.code }})
                </option>
              </select>
            </div>
            <p
              v-if="formErrors.phoneCode"
              class="help is-danger mt-1"
            >
              {{ formErrors.phoneCode }}
            </p>
          </div>
        </div>

        <div class="column">
          <div class="indiefy-field">
            <BaseInput
              v-model="phoneNumber"
              :label="$t('auth.phone-number')"
              :placeholder="$t('auth.phone-number')"
              type="tel"
              data-test="phone-number-input"
              :has-error="!!formErrors.phoneNumber"
              :error-message="formErrors.phoneNumber"
              left-icon="fas fa-phone"
              has-floating-label
              required
              @blur="validatePhoneNumber"
            />
          </div>
        </div>
      </div>

      <!-- Security section -->
      <div class="form-divider mb-4 mt-5">
        <div class="divider-line" />
        <span class="divider-text">{{ $t('auth.security') }}</span>
        <div class="divider-line" />
      </div>

      <div class="indiefy-field">
        <BaseInput
          v-model="password"
          :label="$t('auth.password')"
          :placeholder="$t('auth.password')"
          type="password"
          data-test="password-input"
          :has-error="!!formErrors.password"
          :error-message="formErrors.password"
          left-icon="fas fa-lock"
          has-floating-label
          required
          autocomplete="new-password"
          @blur="validatePassword"
        />
        <div
          v-if="password"
          class="password-strength mt-2"
        >
          <div class="strength-meter">
            <div
              class="strength-value"
              :style="{ width: passwordStrength + '%' }"
              :class="passwordStrengthClass"
            />
          </div>
          <span
            class="strength-text is-size-7"
            :class="'has-text-' + passwordStrengthClass"
          >
            {{ passwordStrengthText }}
          </span>
        </div>
      </div>

      <div class="indiefy-field">
        <BaseInput
          v-model="passwordConfirm"
          :label="$t('auth.confirm-password')"
          :placeholder="$t('auth.confirm-password')"
          type="password"
          data-test="confirm-password-input"
          :has-error="!!formErrors.passwordConfirm"
          :error-message="formErrors.passwordConfirm"
          left-icon="fas fa-lock-open"
          has-floating-label
          required
          autocomplete="new-password"
          @blur="validatePasswordConfirm"
        />
      </div>

      <!-- Terms and Agreement -->
      <div class="indiefy-field mt-5">
        <label class="terms-checkbox">
          <div class="custom-checkbox">
            <input
              v-model="agreeToTerms"
              type="checkbox"
              required
              @change="validateTerms"
            >
            <div class="checkbox-indicator">
              <i class="fas fa-check" />
            </div>
          </div>
          <span class="terms-text">
            {{ $t('auth.agree-to-terms') }}
            <a
              href="#"
              target="_blank"
              class="terms-link"
            >{{ $t('auth.terms-and-conditions') }}</a>
          </span>
        </label>
        <p
          v-if="formErrors.terms"
          class="help is-danger mt-1"
        >
          {{ formErrors.terms }}
        </p>
      </div>

      <div class="mt-5 has-text-centered">
        <BaseButton
          type="submit"
          variant="primary"
          is-fullwidth
          :is-loading="isLoading"
          data-test="signup-button"
          is-gradient
          is-animated
        >
          {{ $t('auth.create-account') }}
        </BaseButton>
      </div>

      <div
        v-if="authError"
        class="error-container mt-4 p-3"
      >
        <i class="fas fa-exclamation-circle mr-2" />
        {{ authError }}
      </div>
    </form>

    <div class="form-divider mb-4 mt-5">
      <div class="divider-line" />
      <span class="divider-text">{{ $t('auth.or-continue-with') }}</span>
      <div class="divider-line" />
    </div>

    <div class="social-signup-buttons mb-4 is-flex">
      <button
        class="social-button google"
        :disabled="isLoading"
        @click="loginWithGoogle"
      >
        <i class="fab fa-google" />
        <span>Google</span>
      </button>
      <button
        class="social-button apple"
        :disabled="isLoading"
        @click="loginWithApple"
      >
        <i class="fab fa-apple" />
        <span>Apple</span>
      </button>
    </div>

    <div class="mt-4 has-text-centered">
      <p class="mb-3">
        {{ $t('auth.already-account') }}
      </p>
      <router-link
        :to="{ name: 'login' }"
        class="sign-in-link"
      >
        {{ $t('auth.sign-in') }}
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import BaseInput from '@/components/ui/BaseInput.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useI18n } from 'vue-i18n';
import countries from '@/data/countries.js';
import publicService from '@/services/publicService.js';
import { usePageSeo } from '@/composables/usePageSeo';

usePageSeo({
  titleKey: 'auth.signup-title',
  descriptionKey: 'auth.signup-description',
});

// Router
const router = useRouter();

// Auth store
const authStore = useAuthStore();
const isLoading = computed(() => authStore.isLoading);
const authError = computed(() => authStore.authError);

// Public service
const locationSettings = ref(null);

// i18n
const { t } = useI18n();

// Only use countries with phone_code and flag
const countryCodes = countries
  .filter(c => c.phone_code && c.flag)
  .map(c => ({
    code: c.phone_code,
    flag: c.flag,
    name: c.name,
    iso: c.code,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Form state
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const phoneCode = ref('+1');
const phoneNumber = ref('');
const password = ref('');
const passwordConfirm = ref('');
const agreeToTerms = ref(false);
const formErrors = ref({
  firstName: '',
  lastName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  password: '',
  passwordConfirm: '',
  terms: '',
});

// Password strength
const passwordStrength = ref(0);
const passwordStrengthClass = ref('danger');
const passwordStrengthText = ref('');

// Calculate password strength
watchEffect(() => {
  if (!password.value) {
    passwordStrength.value = 0;
    passwordStrengthClass.value = 'danger';
    passwordStrengthText.value = '';
    return;
  }

  let strength = 0;
  // Length check
  if (password.value.length >= 8) strength += 25;
  if (password.value.length >= 12) strength += 15;

  // Character type checks
  if (/[0-9]/.test(password.value)) strength += 15;
  if (/[a-z]/.test(password.value)) strength += 15;
  if (/[A-Z]/.test(password.value)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password.value)) strength += 15;

  passwordStrength.value = Math.min(strength, 100);

  if (passwordStrength.value < 30) {
    passwordStrengthClass.value = 'danger';
    passwordStrengthText.value = t('auth.password-weak');
  } else if (passwordStrength.value < 60) {
    passwordStrengthClass.value = 'warning';
    passwordStrengthText.value = t('auth.password-medium');
  } else {
    passwordStrengthClass.value = 'success';
    passwordStrengthText.value = t('auth.password-strong');
  }
});

// Form validation
const validateFirstName = () => {
  if (!firstName.value) {
    formErrors.value.firstName = t('errors.required');
  } else {
    formErrors.value.firstName = '';
  }
};

const validateLastName = () => {
  if (!lastName.value) {
    formErrors.value.lastName = t('errors.required');
  } else {
    formErrors.value.lastName = '';
  }
};

const validateEmail = () => {
  if (!email.value) {
    formErrors.value.email = t('errors.required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
    formErrors.value.email = t('errors.email-invalid');
  } else {
    formErrors.value.email = '';
  }
};

const validatePhoneCode = () => {
  if (!phoneCode.value) {
    formErrors.value.phoneCode = t('errors.required');
  } else if (!/^\+\d{1,4}$/.test(phoneCode.value)) {
    formErrors.value.phoneCode = t('errors.invalid-input');
  } else {
    formErrors.value.phoneCode = '';
  }
};

const validatePhoneNumber = () => {
  if (!phoneNumber.value) {
    formErrors.value.phoneNumber = t('errors.required');
  } else if (!/^\d{10,15}$/.test(phoneNumber.value.replace(/\D/g, ''))) {
    formErrors.value.phoneNumber = t('errors.invalid-input');
  } else {
    formErrors.value.phoneNumber = '';
  }
};

const validatePassword = () => {
  if (!password.value) {
    formErrors.value.password = t('errors.required');
  } else if (password.value.length < 8) {
    formErrors.value.password = t('errors.password-weak');
  } else {
    formErrors.value.password = '';
  }

  // Also validate confirm password if it's been entered
  if (passwordConfirm.value) {
    validatePasswordConfirm();
  }
};

const validatePasswordConfirm = () => {
  if (!passwordConfirm.value) {
    formErrors.value.passwordConfirm = t('errors.required');
  } else if (passwordConfirm.value !== password.value) {
    formErrors.value.passwordConfirm = t('errors.passwords-not-match');
  } else {
    formErrors.value.passwordConfirm = '';
  }
};

const validateTerms = () => {
  if (!agreeToTerms.value) {
    formErrors.value.terms = t('errors.required');
  } else {
    formErrors.value.terms = '';
  }
};

const validateForm = () => {
  validateFirstName();
  validateLastName();
  validateEmail();
  validatePhoneCode();
  validatePhoneNumber();
  validatePassword();
  validatePasswordConfirm();
  validateTerms();

  return !formErrors.value.firstName &&
         !formErrors.value.lastName &&
         !formErrors.value.email &&
         !formErrors.value.phoneCode &&
         !formErrors.value.phoneNumber &&
         !formErrors.value.password &&
         !formErrors.value.passwordConfirm &&
         !formErrors.value.terms;
};

// Form submission
const submitSignup = async () => {
  if (!validateForm()) return;

  authStore.clearError();

  const userData = {
    email: email.value,
    username: email.value,
    phone_code: phoneCode.value,
    phone_number: phoneNumber.value,
    password: password.value,
    name: firstName.value,
    last_name: lastName.value,
  };

  if (localStorage.getItem('utm_campaign')) {
    userData.utm_campaign = localStorage.getItem('utm_campaign');
  }

  const result = await authStore.register(userData);

  localStorage.removeItem('utm_campaign');

  if (result.success) {
    router.push('/dashboard');
  }
};

// Google login
const loginWithGoogle = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithGoogle();

    if (result.success) {
      const redirectPath = '/dashboard';
      router.push(redirectPath);
    }
  } catch (error) {
    console.error('Google login error:', error);
    // Error is already set in the store
  }
};

const loginWithApple = async () => {
  authStore.clearError();

  try {
    const result = await authStore.loginWithApple();

    if (result.success) {
      const redirectPath = '/dashboard';
      router.push(redirectPath);
    }
  } catch (error) {
    console.error('Apple login error:', error);
    // Error is already set in the store
  }
};

onMounted(async () => {
  locationSettings.value = await publicService.getLocationSettings();

  if (locationSettings.value.code) { // Country code
    // Find the country code in the countryCodes array
    const country = countryCodes.find(c => c.iso === locationSettings.value.code);

    if (country) {
      phoneCode.value = country.code;
    }
  }

  if (router.currentRoute.value.query.upgraded) {
    window.fbq && window.fbq('track', 'Subscribe', { currency: 'USD', value: 49.99 });
    window.rdt && window.rdt('track', 'Purchase', { currency: 'USD', value: 49.99 });
    window.gtag && window.gtag('event', 'conversion', { 'send_to': 'AW-323371496/ZyUMCK-wr_ECEOiDmZoB' });
    window.gtag && window.gtag('event', 'conversion', { 'send_to': 'AW-323371496/0WjmCL3ln88YEOiDmZoB' });

    window.gtag && window.gtag('event', 'New Subscription', {
      event_category: 'Subscriptions',
      event_label: 'New Subscription',
    });

    if (router.currentRoute.value.query.email) {
      email.value = router.currentRoute.value.query.email;
    }
  }
});
</script>

<style lang="scss" scoped>
@import '@/styles/abstracts/colors';
@import '@/styles/abstracts/breakpoints';

.signup-view {
  .auth-welcome {
    text-align: center;
    margin-bottom: 2rem;

    .welcome-title {
      font-size: 1.75rem;
      font-weight: 700;
      background: linear-gradient(90deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-bottom: 0.5rem;

      @media screen and (min-width: $tablet) {
        font-size: 2rem;
      }
    }

    .welcome-subtitle {
      color: var(--color-text-light, #64748b);
      font-size: 1rem;

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }
    }
  }
  .title {
    font-size: 1.75rem;
    font-weight: 700;
    background: linear-gradient(90deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.5rem;

    @media screen and (min-width: $tablet) {
      font-size: 2rem;
    }
  }

  .subtitle {
    color: var(--color-text-light, #64748b); /* Fallback color */
    font-weight: 400;

    .body--dark & {
      color: var(--color-text-light, #94a3b8); /* Fallback color */
    }
  }

  // Intro card with design
  .intro-card {
    padding: 1.5rem;
    position: relative;
    border-radius: 16px;
    background: linear-gradient(135deg, var(--color-primary-lighter, rgba(62, 142, 208, 0.1)) 0%, var(--color-secondary-lighter, rgba(124, 58, 237, 0.1)) 100%);
    overflow: hidden;
    box-shadow: 0 8px 20px rgba(var(--color-primary-rgb, 62, 142, 208), 0.1);

    .body--dark & {
      background: linear-gradient(135deg, rgba(var(--color-primary-rgb, 62, 142, 208), 0.1) 0%, rgba(var(--color-secondary-rgb, 124, 58, 237), 0.1) 100%);
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
      z-index: 0;
      opacity: 0.5;
    }
  }

  .intro-icon {
    width: 60px;
    height: 60px;
    margin: 0 auto 1.5rem;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    position: relative;
    box-shadow: 0 8px 20px rgba(var(--color-primary-rgb, 62, 142, 208), 0.3);
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
      border-radius: 50%;
      border: 1px solid rgba(var(--color-primary-rgb, 62, 142, 208), 0.3);
      animation: pulse 2s infinite;
    }
  }

  // Progress indicator
  .progress-info {
    margin-bottom: 1.5rem;

    .progress-badge {
      display: inline-block;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      background-color: var(--color-primary-lighter, rgba(62, 142, 208, 0.2));
      color: var(--color-primary-dark, #2c6592);
      font-size: 0.75rem;
      font-weight: 500;
      margin-bottom: 0.5rem;

      .body--dark & {
        background-color: rgba(var(--color-primary-rgb, 62, 142, 208), 0.3);
        color: var(--color-primary-lighter, #a5ccea);
      }
    }

    .progress-bar {
      height: 6px;
      background-color: rgba(var(--color-primary-rgb, 62, 142, 208), 0.1);
      border-radius: 3px;
      overflow: hidden;

      .progress-value {
        height: 100%;
        background: linear-gradient(90deg, var(--color-primary, #{$indiefy-blue}), var(--color-secondary, #{$indiefy-lavender}));
        border-radius: 3px;
        transition: width 0.3s ease;
      }
    }
  }

  // Form divider
  .form-divider {
    display: flex;
    align-items: center;
    margin: 1.5rem 0;

    .divider-line {
      flex: 1;
      height: 1px;
      background-color: rgba(0, 0, 0, 0.1);

      .body--dark & {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    .divider-text {
      padding: 0 1rem;
      font-size: 0.875rem;
      color: var(--color-text-light, #64748b);

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }
    }
  }

  // Password strength meter
  .password-strength {
    .strength-meter {
      height: 4px;
      background-color: rgba(0, 0, 0, 0.1);
      border-radius: 2px;
      overflow: hidden;
      margin-bottom: 0.25rem;

      .body--dark & {
        background-color: rgba(255, 255, 255, 0.1);
      }

      .strength-value {
        height: 100%;
        border-radius: 2px;
        transition: width 0.3s ease;

        &.danger {
          background-color: #ef4444;
        }

        &.warning {
          background-color: #f59e0b;
        }

        &.success {
          background-color: #10b981;
        }
      }
    }

    .strength-text {
      transition: color 0.3s ease;

      &.has-text-danger {
        color: #ef4444;
      }

      &.has-text-warning {
        color: #f59e0b;
      }

      &.has-text-success {
        color: #10b981;
      }
    }
  }

  // Custom checkbox styling
  .terms-checkbox {
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    user-select: none;

    .custom-checkbox {
      position: relative;
      width: 20px;
      height: 20px;
      margin-right: 12px;
      margin-top: 2px;

      input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
      }

      .checkbox-indicator {
        position: absolute;
        top: 0;
        left: 0;
        width: 20px;
        height: 20px;
        background-color: var(--color-card-background, #ffffff);
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;

        .body--dark & {
          background-color: rgba(0, 0, 0, 0.2);
          border-color: rgba(255, 255, 255, 0.1);
        }

        i {
          color: white;
          font-size: 0.75rem;
          opacity: 0;
          transform: scale(0);
          transition: all 0.2s ease;
        }
      }

      input[type="checkbox"]:checked ~ .checkbox-indicator {
        background-color: var(--color-primary, #{$indiefy-blue});
        border-color: var(--color-primary, #{$indiefy-blue});

        i {
          opacity: 1;
          transform: scale(1);
        }
      }

      input[type="checkbox"]:focus ~ .checkbox-indicator {
        box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb, 62, 142, 208), 0.25);
      }
    }

    .terms-text {
      font-size: 0.9rem;
      line-height: 1.5;
    }

    .terms-link {
      color: var(--color-primary, #{$indiefy-blue});
      font-weight: 500;
      text-decoration: none;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 1px;
        background-color: var(--color-primary, #{$indiefy-blue});
        transform: scaleX(0);
        transform-origin: right;
        transition: transform 0.3s ease;
      }

      &:hover {
        color: var(--color-primary-dark, #2c6592);

        &::after {
          transform: scaleX(1);
          transform-origin: left;
        }
      }
    }
  }

  // Signup features
  .signup-features {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .feature-item {
      display: flex;
      align-items: center;
      font-size: 0.9rem;
      color: var(--color-text-light, #64748b);

      .body--dark & {
        color: var(--color-text-light, #94a3b8);
      }

      i {
        color: #10b981;
        margin-right: 0.5rem;
        font-size: 1rem;
      }
    }
  }

  // Error container
  .error-container {
    background-color: rgba(239, 68, 68, 0.1);
    border-left: 4px solid #ef4444;
    border-radius: 4px;
    color: #ef4444;
    animation: shake 0.5s ease;
  }

  // Social signup buttons
  .social-signup-buttons {
    justify-content: center;
    gap: 1rem;

    .social-button {
      flex: 1;
      height: 44px;
      border-radius: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      border: none;
      cursor: pointer;
      transition: all 0.2s ease;
      font-weight: 500;
      max-width: 160px;

      i {
        margin-right: 0.5rem;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
      }

      &:active {
        transform: translateY(0);
      }

      &.google {
        background-color: #DB4437;
      }

      &.apple {
        background-color: #000000;
      }
    }
  }

  // Sign in link
  .sign-in-link {
    display: inline-block;
    padding: 0.5rem 1.5rem;
    border-radius: 22px;
    background-color: rgba(var(--color-primary-rgb, 62, 142, 208), 0.1);
    color: var(--color-primary, #{$indiefy-blue});
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(var(--color-primary-rgb, 62, 142, 208), 0.2);
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }

    .body--dark & {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  .country-code-dropdown {
    width: 100%;
    .country-select {
      width: 100%;
      min-height: 3.2rem;
      padding: 0.75em 1em;
      border-radius: 8px;
      border: 1px solid #d1d5db;
      font-size: 1.05rem;
      background: #fff;
      appearance: none;
      -webkit-appearance: none;
      line-height: 1.2;
      transition: border 0.2s;
      margin-bottom: 0.25rem;
      &:focus {
        border-color: var(--color-primary, #3e8ed0);
        outline: none;
      }
    }
  }
  @media (max-width: 600px) {
    .country-code-dropdown .country-select {
      font-size: 1rem;
      padding: 0.7em 0.7em;
    }
  }
}

// Animations
@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  70% {
    transform: scale(1.2);
    opacity: 0;
  }
  100% {
    transform: scale(1.2);
    opacity: 0;
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}
</style>